import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Btn, EmptyState, Spinner, Money } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, Plus, Trash2, Pencil } from "lucide-react";

const EMPTY = { name: "", description: "", type: "service", unit_price: 0, vat_rate: 23, unit: "each" };

export default function Products() {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const { data, loading, refetch } = useFetch("/products");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    const payload = { ...form, unit_price: Number(form.unit_price), vat_rate: Number(form.vat_rate) };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); setOpen(false); refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/products/${id}`); refetch(); };

  const items = data?.items || [];
  return (
    <div className="animate-fade-up">
      <PageHeader title="Products & Services" description="Your catalogue of things you sell and do."
        action={<Btn variant="accent" onClick={openNew} data-testid="create-product-button"><Plus size={16} /> New item</Btn>} />
      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={Package} title="No products or services yet" description="Add items to reuse them on quotes and invoices."
          action={<Btn variant="accent" onClick={openNew}><Plus size={16} /> New item</Btn>} />
      ) : (
        <div className="amden-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold hidden sm:table-cell">Type</th>
              <th className="px-4 py-3 font-semibold text-right">Price</th>
              <th className="px-4 py-3 font-semibold text-right hidden sm:table-cell">VAT</th>
              <th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/80" data-testid={`product-row-${p.id}`}>
                  <td className="px-4 py-3"><p className="font-medium text-navy">{p.name}</p><p className="text-xs text-slate-400">{p.description}</p></td>
                  <td className="px-4 py-3 hidden sm:table-cell capitalize text-slate-600">{p.type}</td>
                  <td className="px-4 py-3 text-right"><Money value={p.unit_price} symbol={sym} /> <span className="text-slate-400 text-xs">/{p.unit}</span></td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell text-slate-600 font-mono">{p.vat_rate}%</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-accent hover:bg-blue-50"><Pencil size={15} /></button>
                    <button onClick={() => remove(p.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><label className="text-sm font-medium text-slate-700">Name *</label>
              <input value={form.name} onChange={set("name")} data-testid="product-field-name" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
            <div><label className="text-sm font-medium text-slate-700">Description</label>
              <input value={form.description || ""} onChange={set("description")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700">Type</label>
                <select value={form.type} onChange={set("type")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"><option value="service">Service</option><option value="product">Product</option></select></div>
              <div><label className="text-sm font-medium text-slate-700">Unit</label>
                <input value={form.unit} onChange={set("unit")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
              <div><label className="text-sm font-medium text-slate-700">Price</label>
                <input type="number" value={form.unit_price} onChange={set("unit_price")} data-testid="product-field-price" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono focus:ring-2 focus:ring-blue-500 outline-none" /></div>
              <div><label className="text-sm font-medium text-slate-700">VAT %</label>
                <input type="number" value={form.vat_rate} onChange={set("vat_rate")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono focus:ring-2 focus:ring-blue-500 outline-none" /></div>
            </div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn variant="accent" onClick={save} disabled={saving} data-testid="product-save-button">{saving ? "Saving…" : "Save"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
